/**
 * 구조화된 로거 — Sentry 연동
 *
 * console.log 대신 이 로거를 사용합니다.
 * - debug: dev에서만 콘솔 출력, prod 무시
 * - info:  Sentry breadcrumb 기록
 * - warn:  Sentry breadcrumb
 * - error: Sentry.captureException()
 * - fatal: Sentry.captureException() (level: fatal)
 *
 * userId는 Sentry 글로벌 유저 컨텍스트(authStore에서 setUser)와 별개로,
 * 모든 로그에 자동 포함됩니다. 별도로 data에 넣을 필요 없습니다.
 *
 * @example
 * ```ts
 * import { logger } from '@/lib/logger';
 *
 * logger.info({
 *   domain: 'purchase',
 *   message: '구매 시작',
 *   data: { packageId },
 * });
 *
 * logger.error({
 *   domain: 'purchase',
 *   message: '구매 실패',
 *   error: err,
 *   data: { packageId },
 * });
 * ```
 */
import * as Sentry from "@sentry/react";

import { useAuthStore } from "@/store/authStore";

type LogLevel = "debug" | "info" | "warn" | "error" | "fatal";

interface LogParams {
  domain: string;
  message: string;
  data?: Record<string, unknown>;
  error?: unknown;
}

const isDev = import.meta.env.DEV;

/* eslint-disable no-console -- logger 내부에서만 console 직접 사용 */
const CONSOLE_FN: Record<LogLevel, (...args: unknown[]) => void> = {
  debug: console.debug,
  info: console.log,
  warn: console.warn,
  error: console.error,
  fatal: console.error,
};
/* eslint-enable no-console */

/** 현재 로그인된 유저 ID를 가져온다 (스토어에서 직접 읽기, 리렌더 없음) */
function getUserId(): string | undefined {
  return useAuthStore.getState().user?.id;
}

/** Error가 아닌 값(예: Supabase PostgrestError)에서 사람이 읽을 메시지를 추출한다 */
function extractMessage(error: unknown): string | undefined {
  if (error && typeof error === "object" && "message" in error) {
    const message = (error as { message?: unknown }).message;

    if (typeof message === "string" && message.length > 0) {
      return message;
    }
  }

  return undefined;
}

function log(level: LogLevel, params: LogParams) {
  const { domain, message, data, error } = params;
  const tag = `[${domain}]`;
  const userId = getUserId();

  // data에 userId 자동 병합
  const enrichedData: Record<string, unknown> = { ...data };
  if (userId) {
    enrichedData.userId = userId;
  }

  // 1) dev → 항상 콘솔 출력
  if (isDev) {
    const args: unknown[] = [tag, message, enrichedData];
    if (error) {
      args.push(error);
    }
    CONSOLE_FN[level](...args);
  }

  // 2) debug는 prod에서 무시
  if (!isDev && level === "debug") {
    return;
  }

  // 3) breadcrumb (info, warn) — error가 있으면 메시지를 data에 보존
  if (level === "info" || level === "warn") {
    const errorMessage = error instanceof Error ? error.message : extractMessage(error);

    if (errorMessage) {
      enrichedData.errorMessage = errorMessage;
    }

    Sentry.addBreadcrumb({
      category: domain,
      message,
      level: level === "warn" ? "warning" : "info",
      data: enrichedData,
    });
  }

  // 4) captureException (error, fatal)
  if (level === "error" || level === "fatal") {
    // Error 인스턴스가 아니면(예: Supabase PostgrestError) 원본 메시지를 합성 Error에 담아 유실 방지
    const originalMessage = error instanceof Error ? undefined : extractMessage(error);
    const err =
      error instanceof Error
        ? error
        : new Error(originalMessage ? `${tag} ${message}: ${originalMessage}` : `${tag} ${message}`);

    Sentry.withScope(scope => {
      scope.setTag("domain", domain);
      scope.setLevel(level === "fatal" ? "fatal" : "error");
      if (userId) {
        scope.setTag("userId", userId);
      }
      scope.setExtras(enrichedData);

      // 원본 에러가 Error가 아니면 code/details/hint 등 필드를 컨텍스트로 보존
      if (error != null && !(error instanceof Error) && typeof error === "object") {
        scope.setContext("originalError", error as Record<string, unknown>);
      }

      Sentry.captureException(err);
    });
  }
}

export const logger = {
  debug: (params: LogParams) => log("debug", params),
  info: (params: LogParams) => log("info", params),
  warn: (params: LogParams) => log("warn", params),
  error: (params: LogParams) => log("error", params),
  fatal: (params: LogParams) => log("fatal", params),
};
